// src/AuthProvider.jsx
import React, { createContext, useEffect, useState } from "react";

export const AuthCtx = createContext(null);

const API = `${window.location.origin}`;
const TOKEN_STORAGE_KEY = "token";
const REFRESH_TOKEN_STORAGE_KEY = "refresh_token";
const USER_STORAGE_KEY = "auth_user";
const USER_EMAIL_STORAGE_KEY = "userEmail";
const REFRESH_TOKEN_HEADER = "x-access-token";

async function _pushDeviceHints(token) {
  if (!navigator.userAgentData) return;
  try {
    const data = await navigator.userAgentData.getHighEntropyValues([
      "model", "platform", "platformVersion", "mobile",
    ]);
    const model = data.model || "";
    const platform = data.platform || "";
    if (!model && !platform) return;
    let deviceName = model || platform;
    if (model && platform && model !== platform) {
      deviceName = model;
    }
    await fetch(`${API}/auth/me/sessions/current`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ device_name: deviceName, is_mobile: data.mobile ?? false }),
    });
  } catch { /* Client Hints not supported or denied */ }
}

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  // Returns token from whichever storage it was placed in
  const getToken = () =>
    sessionStorage.getItem(TOKEN_STORAGE_KEY) || localStorage.getItem(TOKEN_STORAGE_KEY);

  const getRefreshToken = () =>
    sessionStorage.getItem(REFRESH_TOKEN_STORAGE_KEY) || localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

  // remember=true  → localStorage  (survives browser restart)
  // remember=false → sessionStorage (cleared when tab/browser closes)
  // remember=null  → keep existing storage location (used for token refreshes)
  const setToken = (t, remember = null) => {
    if (!t) {
      sessionStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      return;
    }
    const inLocal = Boolean(localStorage.getItem(TOKEN_STORAGE_KEY));
    const shouldPersist = remember !== null ? remember : inLocal;
    if (shouldPersist) {
      localStorage.setItem(TOKEN_STORAGE_KEY, t);
      sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    } else {
      sessionStorage.setItem(TOKEN_STORAGE_KEY, t);
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  };

  const setRefreshToken = (t, remember = null) => {
    if (!t) {
      sessionStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
      localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
      return;
    }
    const inLocal = Boolean(localStorage.getItem(TOKEN_STORAGE_KEY));
    const shouldPersist = remember !== null ? remember : inLocal;
    if (shouldPersist) {
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, t);
      sessionStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    } else {
      sessionStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, t);
      localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    }
  };
  const getStoredUser = () => {
    try {
      const raw = localStorage.getItem(USER_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };
  const persistUser = (nextUser) => {
    setUser(nextUser);
    if (nextUser) {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(nextUser));
      if (nextUser.email) {
        localStorage.setItem(USER_EMAIL_STORAGE_KEY, nextUser.email);
      }
      return;
    }
    localStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem(USER_EMAIL_STORAGE_KEY);
  };

  useEffect(() => {
    const originalFetch = window.fetch.bind(window);
    let isRefreshing = false;
    let refreshPromise = null;

    const tryRefresh = async () => {
      if (isRefreshing) return refreshPromise;
      const rt = getRefreshToken();
      if (!rt) return null;
      isRefreshing = true;
      refreshPromise = (async () => {
        try {
          const rr = await originalFetch(`${API}/auth/token/refresh`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refresh_token: rt }),
          });
          if (!rr.ok) return null;
          const { access_token, refresh_token: newRt } = await rr.json();
          setToken(access_token);
          if (newRt) setRefreshToken(newRt);
          return access_token;
        } catch {
          return null;
        } finally {
          isRefreshing = false;
          refreshPromise = null;
        }
      })();
      return refreshPromise;
    };

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      const nextToken = response?.headers?.get(REFRESH_TOKEN_HEADER);
      if (nextToken) setToken(nextToken);
      if (response.status === 401 && getToken()) {
        const url = typeof args[0] === "string" ? args[0] : (args[0]?.url ?? "");
        const isLoginFlow = /\/auth\/(login|register|verify|resend|forgot|reset)/.test(url);
        if (!isLoginFlow) {
          const newToken = await tryRefresh();
          if (newToken) {
            const [input, init = {}] = args;
            const newHeaders = new Headers(init.headers || (input instanceof Request ? input.headers : {}));
            newHeaders.set("Authorization", `Bearer ${newToken}`);
            return originalFetch(input, { ...init, headers: newHeaders });
          }
          setToken(null);
          setRefreshToken(null);
          persistUser(null);
        }
      }
      return response;
    };
    return () => { window.fetch = originalFetch; };
  }, []);

  useEffect(() => {
    const init = async () => {
      const t = getToken();
      if (!t) { setReady(true); return; }
      const cachedUser = getStoredUser();
      try {
        const r = await fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${t}` } });
        const nextToken = r.headers?.get(REFRESH_TOKEN_HEADER);
        if (nextToken) setToken(nextToken);
        if (r.ok) { persistUser(await r.json()); return; }
        if (r.status === 401 || r.status === 403) {
          const rt = getRefreshToken();
          if (rt) {
            try {
              const rr = await fetch(`${API}/auth/token/refresh`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ refresh_token: rt }),
              });
              if (rr.ok) {
                const { access_token, refresh_token: newRt } = await rr.json();
                setToken(access_token);
                if (newRt) setRefreshToken(newRt);
                const me2 = await fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${access_token}` } });
                if (me2.ok) { persistUser(await me2.json()); return; }
              }
            } catch { /* refresh failed */ }
          }
          setToken(null);
          setRefreshToken(null);
          persistUser(null);
          return;
        }
        if (cachedUser) persistUser(cachedUser);
      } catch {
        if (cachedUser) persistUser(cachedUser);
      } finally {
        setReady(true);
      }
    };
    init();
  }, []);

  useEffect(() => {
    const INTERVAL = 30_000;
    const ping = async () => {
      const t = getToken();
      if (!t) return;
      try {
        await fetch(`${API}/auth/heartbeat`, {
          method: "POST",
          headers: { Authorization: `Bearer ${t}` },
        });
      } catch { /* network error — ignore */ }
    };
    ping();
    const id = setInterval(ping, INTERVAL);
    return () => clearInterval(id);
  }, []);

  const parseError = async (r) => {
    const contentType = r.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      const data = await r.json().catch(() => null);
      if (data) {
        if (typeof data.detail === "string") return data.detail;
        if (Array.isArray(data.detail)) {
          const messages = data.detail.map((item) => item.msg).filter(Boolean);
          if (messages.length) return messages.join("; ");
        }
      }
    }
    const text = await r.text().catch(() => "");
    return text || "Request failed";
  };

  const login = async (email, password, rememberMe = false) => {
    const r = await fetch(`${API}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, remember_me: rememberMe }),
    });
    if (!r.ok) throw new Error(await parseError(r));

    const { access_token, refresh_token } = await r.json();
    setToken(access_token, rememberMe);
    if (refresh_token) setRefreshToken(refresh_token, rememberMe);

    const meResponse = await fetch(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${access_token}` },
    });
    const nextToken = meResponse.headers?.get(REFRESH_TOKEN_HEADER);
    if (nextToken) setToken(nextToken);
    if (!meResponse.ok) throw new Error(await parseError(meResponse));
    const me = await meResponse.json();
    persistUser(me);

    // Push real device model via Client Hints (Chrome 107+)
    _pushDeviceHints(access_token);

    return me;
  };

  // Returns { needsVerification: true, email } when SMTP is enabled (email not yet verified)
  const registerStudent = async (fio, email, password) => {
    const res = await fetch(`${API}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fio, email, password }),
    });
    if (!res.ok) throw new Error(await parseError(res));
    const userData = await res.json();
    if (!userData.email_verified) {
      return { needsVerification: true, email, resent: !!userData.resent, tgLink: userData.tg_link || null };
    }
    await login(email, password);
    return { needsVerification: false };
  };

  // Returns { needsVerification: true, email } or throws
  const register = async (fio, email, password, inviteCode = "") => {
    const isInvite = Boolean((inviteCode || "").trim());
    const endpoint = isInvite ? "/auth/register-invite" : "/auth/register";
    const body = isInvite
      ? { fio, email, password, invite_code: inviteCode.trim() }
      : { fio, email, password };

    const res = await fetch(`${API}${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!res.ok) throw new Error(await parseError(res));

    const userData = await res.json();
    if (!userData.email_verified) {
      return { needsVerification: true, email, resent: !!userData.resent, tgLink: userData.tg_link || null };
    }
    await login(email, password);
    return { needsVerification: false };
  };

  // Confirms the 6-char code and logs the user in, returning the user object
  const verifyEmail = async (email, code) => {
    const res = await fetch(`${API}/auth/verify-email/confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, code }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.detail || "Неверный или просроченный код");
    }
    const { access_token } = await res.json();
    setToken(access_token);

    const meResponse = await fetch(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${access_token}` },
    });
    const nextToken = meResponse.headers?.get(REFRESH_TOKEN_HEADER);
    if (nextToken) setToken(nextToken);
    if (!meResponse.ok) throw new Error(await parseError(meResponse));
    const me = await meResponse.json();
    persistUser(me);
    return me;
  };

  // Re-sends verification code (rate-limited server-side to 1/min)
  const resendVerification = async (email) => {
    const res = await fetch(`${API}/auth/verify-email/send`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.detail || "Не удалось отправить код");
    }
    return await res.json();
  };

  const logout = () => {
    setToken(null);
    setRefreshToken(null);
    persistUser(null);
    Object.keys(localStorage)
      .filter((k) => k.startsWith("audassist."))
      .forEach((k) => localStorage.removeItem(k));
  };

  return (
    <AuthCtx.Provider value={{
      user, setUser: persistUser, ready,
      login, register, registerStudent, logout,
      verifyEmail, resendVerification,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}
